import classNames from "classnames";
import { useState } from "react";
import { Button } from "./button";
import { getCookie, writeCookie } from "./cookie-notice";
import { Group } from "./group";
import { IconChevronDown, IconChevronUp } from "./icons";

const COOKIE_PREFIX = "banner-dismissed-";

export const CollapsibleBanner = ({
  title,
  children,
  icon,
  feature,
  defaultExpanded = false,
  className = "",
  extraActions,
  dismissable = true,
  persistDismissed = true,
}: {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  feature: string;
  defaultExpanded?: boolean;
  dismissable?: boolean;
  persistDismissed?: boolean;
  className?: string;
  extraActions?: React.ReactNode;
}) => {
  const [dismissed, setDismissed] = useState(() => {
    if (!dismissable) return false;
    if (!persistDismissed) return false;

    return getCookie(`${COOKIE_PREFIX}${feature}`) === "1";
  });
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (dismissed) return null;

  const handleDismiss = () => {
    if (dismissable) {
      setDismissed(true);
    }

    if (persistDismissed) {
      writeCookie(`${COOKIE_PREFIX}${feature}`, "1", 365);
    }
  };

  const ChevronIcon = expanded ? IconChevronUp : IconChevronDown;

  return (
    <div
      role="region"
      aria-label={title}
      className={classNames(
        "rounded border border-indigo bg-indigo-100 px-4 py-4 text-black",
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={expanded}
        className="flex w-full items-center"
        onClick={() => setExpanded(!expanded)}
      >
        {icon ? <div className="mr-3">{icon}</div> : null}
        <span className="flex-1 text-left text-lg font-semibold">{title}</span>
        <ChevronIcon color="black" variant="base" />
      </button>

      {expanded ? (
        <div className={classNames("mt-2", icon ? "ml-9" : "ml-0")}>
          <div>{children}</div>
          <Group
            variant="horizontal"
            size="sm"
            className="items-center gap-2 mt-2"
          >
            {dismissable ? (
              <Button variant="white" size="sm" onClick={handleDismiss}>
                Dismiss
              </Button>
            ) : null}
            {extraActions || null}
          </Group>
        </div>
      ) : null}
    </div>
  );
};
